import { Link } from 'react-router-dom';

export function TermsAndConditions() {
  return (
    <div className="min-h-dvh bg-surface">
      <nav className="fixed top-0 left-0 right-0 bg-white border-b border-border px-8 py-4 flex items-center justify-between z-[100]">
        <Link
          to="/landing"
          className="flex items-center gap-2 no-underline"
        >
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-8 w-8"
          />
          <span className="text-xl font-bold text-primary">Yaruta</span>
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Privacy
          </Link>
          <Link
            to="/"
            className="text-sm text-muted-foreground no-underline hover:text-foreground"
          >
            Sign In
          </Link>
        </div>
      </nav>

      <main className="max-w-[720px] mx-auto px-6 pt-24 pb-16">
        <h1 className="text-4xl font-bold text-foreground mb-2">
          Terms & Conditions
        </h1>
        <p className="text-sm text-muted-foreground mb-10">
          Effective Date: September 2026
        </p>

        <p className="text-base text-foreground leading-relaxed mb-10">
          By using Yaruta through yaruta.app, getyaruta.com, or related product
          experiences, you agree to these Terms.
        </p>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            1. Service Overview
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            Yaruta helps households organize family logistics by converting
            approved email communications into summaries, reminders, and shared
            schedules.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            2. User Responsibility
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            You are responsible for:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed">
            <li>
              Connecting only inboxes and accounts you are authorized to use
            </li>
            <li>Reviewing extracted tasks and events before committing them</li>
            <li>Managing who has access to your household workspace</li>
            <li>Ensuring collaborator permissions are appropriate</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            3. AI-Generated Suggestions
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Yaruta uses AI to suggest summaries, tasks, reminders, and calendar
            events based on connected email communications.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            These outputs are recommendations only and may not always be
            accurate.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Parents remain responsible for reviewing all extracted information
            before relying on it.
          </p>
          <p className="text-base font-semibold text-primary leading-relaxed">
            AI suggests, parents approve.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            4. Availability
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            We may update, modify, or temporarily suspend parts of the service
            as the MVP evolves.
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            5. Acceptable Use
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            You may not:
          </p>
          <ul className="list-disc pl-6 flex flex-col gap-2 text-base text-foreground leading-relaxed">
            <li>Misuse connected inbox permissions</li>
            <li>Attempt unauthorized access</li>
            <li>Upload harmful code</li>
            <li>Use Yaruta for unlawful purposes</li>
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            6. Limitation of Liability
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Yaruta is provided "as is" during MVP and beta phases.
          </p>
          <p className="text-base text-foreground leading-relaxed">
            We are not liable for missed deadlines, school events, household
            scheduling conflicts, or decisions based on unreviewed AI-generated
            outputs.
          </p>
        </section>

        {/* Twilio RCS sender review (YAR-461): the messaging program's terms
            — brand, message types, frequency, rates, HELP, STOP, the
            number-change clause and the Privacy Policy link. Mirrored in
            settings/legal/TermsOfServiceBody.tsx. */}
        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            7. SMS/RCS Communications
          </h2>
          <p className="text-base text-foreground leading-relaxed mb-3">
            If you opt in to Yaruta text messaging, you agree to receive
            recurring service-related SMS and/or RCS messages from Yaruta about
            family schedules, events, tasks, reminders, daily digests, and items
            requiring review.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Message frequency varies. Message and data rates may apply.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            Reply HELP for help or contact{' '}
            <a
              href="mailto:hello@yaruta.app"
              className="text-primary underline hover:text-primary-hover"
            >
              hello@yaruta.app
            </a>
            . Reply STOP to cancel and opt out of future recurring messages. You
            may opt out at any time.
          </p>
          <p className="text-base text-foreground leading-relaxed mb-3">
            You will not be held responsible for failing to opt out of Yaruta
            messages solely because your mobile phone number changes or is
            deactivated.
          </p>
          <p className="text-base text-foreground leading-relaxed">
            For information about how Yaruta handles your personal information,
            please review our{' '}
            <Link
              to="/privacy"
              className="text-primary underline hover:text-primary-hover"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </section>

        <section className="mb-10">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            8. Contact
          </h2>
          <p className="text-base text-foreground leading-relaxed">
            For support:{' '}
            <a
              href="mailto:hello@yaruta.app"
              className="text-primary underline hover:text-primary-hover"
            >
              hello@yaruta.app
            </a>
          </p>
        </section>
      </main>

      <footer className="bg-foreground text-muted py-8 px-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <img
            src="/logo.svg"
            alt="Yaruta"
            className="h-6 w-6 brightness-0 invert"
          />
          <span className="text-lg font-bold text-white">Yaruta</span>
        </div>
        <div className="flex justify-center gap-6 mb-3">
          <Link
            to="/privacy"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Privacy Policy
          </Link>
          <Link
            to="/terms"
            className="text-sm text-muted-foreground no-underline hover:text-white"
          >
            Terms & Conditions
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          &copy; {new Date().getFullYear()} Yaruta. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
